import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import useSectionsCollapse from '../../../../hooks/useSectionsCollapse.js';
import { CategorySection } from './styles.js';
import Category from './Category.js';
import { getCheckboxCategories } from '../../../../ducks/extras/selectors';

const CheckBoxCategories = ({ device, features, onChange }) => {
  const initialRender = useRef(true);
  const visibleCount = device === 'mobile' ? 3 : 6;
  const categories = useSelector(getCheckboxCategories);
  const categoryIds = categories.map(category => category.id);

  // Custom Hook para manejar el colapso de secciones
  const { registerRef, getMaxHeight, isCollapsed, setCollapsed } = useSectionsCollapse({
    visibleItems: visibleCount,
    itemSelector: '.feature-item',
    dependencies: [categoryIds],
    sections: categoryIds,
    initialCollapse: true
  });

  // Función para verificar si una categoría tiene características seleccionadas que estarían ocultas
  const hasHiddenCheckedFeatures = categoryOptions => {
    if (!features || !categoryOptions) return false;
    // Si hay menos opciones que el límite visible, no habría elementos ocultos
    if (categoryOptions.length <= visibleCount) return false;
    // Verificamos si hay características seleccionadas entre las ocultas (después del índice visibleCount)
    return categoryOptions
      .slice(visibleCount) // Tomamos solo las opciones que estarían ocultas
      .some(option => {
        const feature = features.find(f => f.id === option.id);
        return feature && feature.isChecked;
      });
  };

  // Efecto para expandir categorías con elementos seleccionados ocultos en el primer render
  useEffect(() => {
    if (initialRender.current && features && features.length > 0) {
      // Timeout para asegurar que todos los datos estén disponibles
      setTimeout(() => {
        categories.forEach(({ id, options }) => {
          if (isCollapsed(id) && hasHiddenCheckedFeatures(options)) {
            setCollapsed(id, false);
          }
        });
        initialRender.current = false;
      }, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, features, isCollapsed, setCollapsed]);

  return (
    <CategorySection data-publi='checkbox-categories-section'>
      {categories.map(({ id, options }) => {
        if (options.length === 0) return null; // Evitar renderizar categorías sin opciones
        return (
          <Category
            key={id}
            id={id}
            options={options}
            device={device}
            features={features}
            onChange={onChange}
            registerRef={registerRef}
            getMaxHeight={getMaxHeight}
            isCollapsed={isCollapsed}
            setCollapsed={setCollapsed}
            visibleCount={visibleCount}
          />
        );
      })}
    </CategorySection>
  );
};

export default CheckBoxCategories;
